import * as Y from "yjs"

// The shared notes live in the "notes" XML fragment, which TipTap's
// Collaboration extension is bound to. Two things write plain text into it
// from outside the editor: the practice classroom's sample text (#945) and
// the restore of the notes saved on the lesson (#1034). Both write only into
// an EMPTY fragment, so anything a person already wrote wins.

export const NOTES_FIELD = "notes"

// Bump when the way text becomes paragraphs changes: it is part of the fixed
// client id below, so two versions never produce "the same" restore.
const RESTORE_VERSION = "v1"

// One paragraph per line; a "\n" inside a line is a hard break.
function paragraphFor(line: string): Y.XmlElement {
  const paragraph = new Y.XmlElement("paragraph")
  const parts = line.split("\n")
  const children: Array<Y.XmlText | Y.XmlElement> = []
  parts.forEach((part, i) => {
    if (i > 0) children.push(new Y.XmlElement("hardBreak"))
    if (part) children.push(new Y.XmlText(part))
  })
  if (children.length) paragraph.insert(0, children)
  return paragraph
}

export function insertParagraphs(fragment: Y.XmlFragment, lines: string[]): void {
  fragment.insert(0, lines.map(paragraphFor))
}

// The text `editor.getText()` returns puts a blank line between paragraphs.
export function paragraphsOfText(text: string): string[] {
  return text.split("\n\n")
}

// A stable unsigned 32-bit number for a string (FNV-1a), never 0.
function hash32(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0) || 1
}

// Put the saved notes back into an empty editor. The update is built in a
// scratch doc whose client id comes from the lesson and the text, so two
// participants who restore at the same moment write the very same operations:
// Yjs sees one insert, not two, and the notes show once (not twice).
// Returns whether it wrote anything.
export function restoreSavedNotes(doc: Y.Doc, lessonId: string, savedText: string): boolean {
  if (!savedText.trim()) return false
  const fragment = doc.getXmlFragment(NOTES_FIELD)
  if (fragment.length > 0) return false

  const scratch = new Y.Doc()
  scratch.clientID = hash32(`${RESTORE_VERSION}:${lessonId}:${savedText}`)
  insertParagraphs(scratch.getXmlFragment(NOTES_FIELD), paragraphsOfText(savedText))
  const update = Y.encodeStateAsUpdate(scratch)
  scratch.destroy()
  Y.applyUpdate(doc, update)
  return true
}
